import {
  BoltIcon,
  ClockIcon,
  EllipsisVerticalIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useRef, useState } from 'react';

import { JobSurge, paySurgeClient, surgeErrorMessage } from '../api';
import { formatCents, surgeReachNote, untilLabel } from '../format';
import useJobSurge from '../hooks/useJobSurge';

import ManageSurgePayModal from './ManageSurgePayModal';
import OverrideSurgeModal from './OverrideSurgeModal';
import SurgeToast, { SurgeToastMessage } from './SurgeToast';

import '../paySurge.css';

type Props = {
  jobId: number | string;
  hiredCount: number;
  quantity: number;
  /** Tenant Admins act on surge; everyone else sees nothing. */
  canManage: boolean;
  /**
   * Called after every write that succeeded. A surge write changes the JOB's
   * pay rate, which the host page may hold in its own query (the Job page's
   * Apollo GetJob) — this is its cue to refetch rather than show the old rate.
   */
  onChanged?: () => void;
};

/**
 * The job page's surge pay banner (GRAVY-10443), in its three designed states:
 *
 *   upcoming  "Pay bump coming up!" and when          Figma 38478:34438
 *   active    "Surge pay is active" and by how much    38482:34911
 *   paused    "Pay surge paused", toggle on, amber     40551:177889
 *
 * Shown only while there is something to say: the schedule is running or
 * paused, the shift has not started, and it is still short. A full shift has
 * no surge story, so it gets no banner.
 */
const SurgeBanner = ({
  jobId,
  hiredCount,
  quantity,
  canManage,
  onChanged,
}: Props) => {
  const { data: surge, apply } = useJobSurge(jobId, canManage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [dialog, setDialog] = useState<null | 'override' | 'manage'>(null);
  const [toast, setToast] = useState<SurgeToastMessage | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  if (!canManage || !surge)
    return <SurgeToast toast={toast} onDismiss={dismissToast} />;

  const upcomingShift =
    surge.first_shift_start_at != null &&
    new Date(surge.first_shift_start_at).getTime() > Date.now();
  const short = hiredCount < quantity;
  const exhausted = noTierLeft(surge);
  const visible =
    upcomingShift &&
    short &&
    (surge.status === 'active' ||
      surge.status === 'paused' ||
      (surge.status === 'upcoming' &&
        (surge.next_tier != null || surge.due_tier != null || exhausted)));

  // Pausing something that is not running is nonsense, and offering the control
  // is itself part of the false impression (GRAVY-10869). "Manage surge pay"
  // stays — editing the schedule before posting is exactly the right thing.
  // Same when the platform automation is switched off: nothing would fire, so
  // there is nothing to pause. Override and Manage still work by hand.
  // And once no tier is left to fire (GRAVY-10879) there is nothing to pause
  // either; Override is then the only way pay rises before the shift.
  const canPause =
    surge.will_apply && surge.automation_on !== false && !exhausted;

  const applied = (next: JobSurge) => {
    apply(next);
    onChanged?.();
  };

  const togglePause = async () => {
    setBusy(true);
    setError(null);
    try {
      const paused = surge.status === 'paused';
      const next: JobSurge = paused
        ? await paySurgeClient.resumeJobSurge(surge.job_id)
        : await paySurgeClient.pauseJobSurge(surge.job_id);
      applied(next);
      // Figma 40563:187694.
      if (!paused) {
        setToast({
          kind: 'paused',
          title: 'Pay surge paused',
          message: `Shift #${surge.job_id} won’t auto-increase pay — turn it back on anytime.`,
        });
      }
    } catch (e) {
      setError(
        surgeErrorMessage(e) ?? 'Could not change surge pay. Please try again.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {visible && (
        <div
          className={`paysurge-banner is-${surge.status}`}
          data-testid="surgeBanner"
          data-status={surge.status}
        >
          <BannerMessage surge={surge} />

          <div className="paysurge-bannercontrols">
            {canPause && (
              <>
                <span
                  className="paysurge-bannertoggle"
                  id={`surge-pause-${surge.job_id}`}
                >
                  {surge.status === 'paused'
                    ? 'Pay surge on pause'
                    : 'Pause pay surge'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={surge.status === 'paused'}
                  aria-labelledby={`surge-pause-${surge.job_id}`}
                  className="paysurge-switch is-pause"
                  data-testid="surgePauseToggle"
                  disabled={busy}
                  onClick={togglePause}
                />
              </>
            )}
            <div className="paysurge-menuwrap" ref={menuRef}>
              <button
                type="button"
                className="paysurge-iconbtn"
                aria-label="More surge pay actions"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((o) => !o)}
              >
                <EllipsisVerticalIcon aria-hidden="true" />
              </button>
              {menuOpen && (
                <div className="paysurge-menu" role="menu">
                  <button
                    type="button"
                    role="menuitem"
                    className="paysurge-menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setDialog('override');
                    }}
                  >
                    Override amount
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className="paysurge-menuitem"
                    onClick={() => {
                      setMenuOpen(false);
                      setDialog('manage');
                    }}
                  >
                    Manage surge pay
                  </button>
                </div>
              )}
            </div>
          </div>
          {error && (
            <p className="paysurge-bannererror" role="alert">
              {error}
            </p>
          )}
        </div>
      )}

      {dialog === 'override' && (
        <OverrideSurgeModal
          surge={surge}
          onSaved={applied}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog === 'manage' && (
        <ManageSurgePayModal
          surge={surge}
          onSaved={applied}
          onClose={() => setDialog(null)}
        />
      )}
      <SurgeToast toast={toast} onDismiss={dismissToast} />
    </>
  );
};

/*
 * GRAVY-10879. Surge is running, nothing has been applied, and no tier is left
 * to fire — neither ahead (`next_tier`) nor waiting for the sweep (`due_tier`).
 * Reached by resuming after every remaining tier's moment has passed: resume
 * is forward only, so those tiers are stamped skipped (Product's no-backfill
 * rule). This state used to match no branch, and the banner vanished with
 * Pause, Override and Manage — leaving ops no way to raise pay from here.
 *
 * Only for a schedule that has tiers: one with none has never promised a rise.
 */
const noTierLeft = (surge: JobSurge) =>
  surge.status === 'upcoming' &&
  surge.next_tier == null &&
  surge.due_tier == null &&
  surge.tiers.length > 0;

/*
 * What the raise reaches, appended where the banner talks about money changing
 * (Decision 2, 2026-09-30). Rendered only for a multi-shift job — on a
 * single-shift job the banner's own "this shift" is already the whole truth.
 */
const ReachNote = ({ surge }: { surge: JobSurge }) =>
  surge.shifts_count > 1 ? (
    <span className="paysurge-bannerreach">
      {' '}
      {surgeReachNote(surge.shifts_count)}
    </span>
  ) : null;

// The sentence, per state, in the design's words.
const BannerMessage = ({ surge }: { surge: JobSurge }) => {
  /*
   * GRAVY-10869. A private/unposted job still gets a schedule and a preview of
   * it, but `Snapshot.pending_jobs` only snapshots POSTED jobs — so the sweep
   * never touches this one and the countdown would reach zero with nothing
   * happening. Say what is true instead of promising a rise.
   *
   * Deliberately not hidden: the schedule is real and ops configure it before
   * posting. What was wrong was presenting a prediction as a commitment.
   */
  /*
   * The platform surge automation (`pay-escalation` kill switch) is off, so the
   * sweep runs for no job: a countdown or "within the next minute" would never
   * come true, and neither would "post it publicly and pay will begin rising".
   * Active and paused keep their own sentences — both are still true.
   * A missing field (older backend) reads as on.
   */
  if (
    surge.automation_on === false &&
    surge.status !== 'active' &&
    surge.status !== 'paused'
  ) {
    return (
      <p className="paysurge-bannertext">
        <ClockIcon aria-hidden="true" />
        <strong className="is-warning">
          Automatic surge is switched off right now.
        </strong>{' '}
        <span>Pay won’t rise on its own.</span>
      </p>
    );
  }

  if (!surge.will_apply) {
    return (
      <p className="paysurge-bannertext">
        <ClockIcon aria-hidden="true" />
        <strong className="is-warning">Surge pay is set up</strong>{' '}
        <span>
          It won’t start while this shift is private — post it publicly and pay
          will begin rising as it gets closer.
        </span>
      </p>
    );
  }

  if (surge.status === 'paused') {
    return (
      <p className="paysurge-bannertext">
        <PauseIcon aria-hidden="true" />
        <strong className="is-paused">Pay surge paused.</strong> This shift
        won’t auto-increase pay, which may slow down fill rate.
      </p>
    );
  }

  if (surge.status === 'active') {
    return (
      <p className="paysurge-bannertext">
        <BoltIcon aria-hidden="true" />
        <strong className="is-warning">Surge pay is active</strong>{' '}
        <span>
          This shift is still unfilled — pay is now{' '}
          <b>{formatCents(surge.applied_cents)}/hr</b> higher to attract
          workers.
          <ReachNote surge={surge} />
        </span>
      </p>
    );
  }

  // GRAVY-10879 — see `noTierLeft`. Say plainly that nothing more will happen
  // on its own, and point at the one control that can still raise pay.
  if (noTierLeft(surge)) {
    const skipped = surge.tiers.some((tier) => tier.state === 'skipped');
    return (
      <p className="paysurge-bannertext" data-testid="surgeNoTierLeft">
        <ClockIcon aria-hidden="true" />
        <strong className="is-warning">
          No surge tiers left before this shift.
        </strong>{' '}
        <span>
          {skipped
            ? 'The tiers that came due while surge was paused were skipped, so pay won’t rise on its own.'
            : 'Every tier’s time has passed, so pay won’t rise on its own.'}{' '}
          Use Override amount to raise it now.
        </span>
      </p>
    );
  }

  /*
   * GRAVY-10871. A tier whose moment has passed, but which the sweep (every
   * minute) has not applied yet. `next_tier` only looks forward, so this state
   * used to match no branch and the banner vanished — Pause, Override and
   * Manage with it — on a shift about to surge. It is what happens next.
   */
  if (surge.due_tier) {
    const step =
      Math.min(surge.due_tier.rate_cents, surge.max_cents) -
      surge.applied_cents;
    return (
      <p className="paysurge-bannertext">
        <ClockIcon aria-hidden="true" />
        <strong className="is-warning">Pay bump coming up!</strong>{' '}
        <span>
          This shift is still unfilled — pay rises{' '}
          {surge.applied_cents > 0 ? 'another ' : ''}
          <b>{formatCents(step)}/hr</b> within the next minute.
          <ReachNote surge={surge} />
        </span>
      </p>
    );
  }

  const next = surge.next_tier!;
  // Tiers are absolute, so "another" is the step from what is paid now.
  const step = next.rate_cents - surge.applied_cents;
  return (
    <p className="paysurge-bannertext">
      <ClockIcon aria-hidden="true" />
      <strong className="is-warning">Pay bump coming up!</strong>{' '}
      <span>
        If this shift stays unfilled, pay rises{' '}
        {surge.applied_cents > 0 ? 'another ' : ''}
        <b>{formatCents(step)}/hr</b> in {untilLabel(next.fires_at ?? '')}.
        <ReachNote surge={surge} />
      </span>
    </p>
  );
};

export default SurgeBanner;
